import { useState } from "react";
import { useDogs } from "@/context/DogsContext";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Pencil, Heart } from "lucide-react";
import AddDogDialog from "@/components/app/AddDogDialog";

export default function DogPickerBar() {
  const { dogs, activeDogId, activeDog, selectDog } = useDogs();
  const { user } = useAuth();
  const hideRainbowBridge = !!user?.rainbow_bridge_hidden;
  const [editOpen, setEditOpen] = useState(false);

  if (!dogs.length) return null;

  // Only the active, owned dog gets the inline pencil — keeps the bar tidy
  // and avoids the ambiguity of "which dog am I editing?" Shared dogs are
  // read-only here regardless of share_permission (recipients can edit via
  // their own Overview hero if their share is "edit").
  const canEditActive = !!activeDog && !activeDog.shared;

  return (
    <div className="px-4 sm:px-8 py-4 border-b border-border bg-card/60">
      <div className="flex items-center gap-3 overflow-x-auto pb-1" data-testid="dog-picker-bar">
        {dogs.map((dog) => {
          const active = dog.id === activeDogId;
          const initials = (dog.name || "?")
            .split(" ")
            .map((p) => p[0])
            .slice(0, 2)
            .join("")
            .toUpperCase();
          const showEdit = active && canEditActive;

          // Two sibling buttons inside a styled wrapper — keeps HTML valid
          // (no button-in-button) and lets each click target the right action.
          return (
            <div
              key={dog.id}
              className={`group flex items-center rounded-full border transition-all overflow-hidden ${
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border hover:bg-muted text-foreground"
              }`}
            >
              <button
                onClick={() => selectDog(dog.id)}
                data-testid={`dog-pick-${dog.id}`}
                className="flex items-center gap-2.5 pl-1 pr-3 py-1"
              >
                <Avatar className="h-8 w-8 ring-1 ring-border">
                  {dog.photo_url ? <AvatarImage src={dog.photo_url} alt={dog.name} /> : null}
                  <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium whitespace-nowrap">{dog.name}</span>
                {dog.rainbow_bridge && !hideRainbowBridge ? (
                  <span
                    title="At the Rainbow Bridge"
                    aria-label={`${dog.name} — at the Rainbow Bridge`}
                    data-testid={`dog-pick-bridge-${dog.id}`}
                    className={`inline-flex items-center justify-center h-4 w-4 rounded-full ${
                      active ? "bg-primary-foreground/20 text-primary-foreground" : "bg-secondary text-primary"
                    }`}
                  >
                    <Heart size={10} fill="currentColor" strokeWidth={0} />
                  </span>
                ) : null}
                {dog.shared ? (
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${active ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground"}`}>shared</span>
                ) : null}
              </button>
              {showEdit && (
                <button
                  onClick={() => setEditOpen(true)}
                  data-testid={`dog-pick-edit-${dog.id}`}
                  aria-label={`Edit ${dog.name}`}
                  title="Edit basics"
                  className="h-8 w-8 flex items-center justify-center text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10 transition-colors border-l border-primary-foreground/20"
                >
                  <Pencil size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Mounted at the bar level so any chip's pencil opens the same dialog.
          Keyed by the active dog id so state resets when the user switches. */}
      {canEditActive && (
        <AddDogDialog
          key={activeDog.id}
          open={editOpen}
          onOpenChange={setEditOpen}
          editDog={activeDog}
        />
      )}
    </div>
  );
}
